package com.trevorwiebe.timesheet.mileage.presentation

import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.mileage.data.ExtractedReceiptData
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.minus

class PendingImageBytes(val bytes: ByteArray, val isNewUpload: Boolean = true)

data class MileageUiState(
    val entries: List<MileageEntry> = emptyList(),
    val currentUser: DatabaseUserModel? = null,
    val isLoading: Boolean = true,
    val error: String? = null,
    val showAddDialog: Boolean = false,
    val editingEntry: MileageEntry? = null,
    val addEntryForm: AddMileageFormState = AddMileageFormState(),
    val equipment: List<Equipment> = emptyList(),
    val locations: List<Location> = emptyList(),
    val userList: List<DatabaseUserModel> = emptyList(),
    val pendingEntry: MileageEntry? = null,
    val pendingLocation: Location? = null,
    val pendingAutoStart: MileageEntry? = null,
    val pendingOdometerDifference: Long? = null,
    val pendingIsEdit: Boolean = false,
    val showOdometerWarning: Boolean = false,
    val odometerError: String? = null,
    val isSaving: Boolean = false,
    val entryToDelete: MileageEntry? = null,
    val showDeleteConfirm: Boolean = false,
    val filter: MileageFilter = MileageFilter(),
    val isExtractingReceipt: Boolean = false,
    val isLoadingReceiptImage: Boolean = false,
    val extractedReceiptData: ExtractedReceiptData? = null,
    val pendingReceiptBytes: PendingImageBytes? = null,
    val showPhotoViewer: Boolean = false,
    val isEditingLocked: Boolean = false,
) {
    val isElevated: Boolean
        get() = currentUser?.role == UserRole.ADMIN || currentUser?.role == UserRole.DISPATCHER

    val isAdmin: Boolean
        get() = currentUser?.role == UserRole.ADMIN

    val tripTypeOptions: List<TripTypeOption>
        get() = TripTypeOption.entries.filter {
            it.tripType != TripType.SERVICE || isAdmin || editingEntry?.tripType == TripType.SERVICE
        }

    /**
     * Whether the entry being edited/created will end up with a receipt on save. An edited
     * entry's existing receipt is downloaded into [pendingReceiptBytes] asynchronously, so a
     * still-running download also counts.
     */
    val hasFuelReceipt: Boolean
        get() = pendingReceiptBytes != null || isLoadingReceiptImage

    /**
     * True when [filter] differs from its default — an equipment/employee is chosen, a proper
     * subset of trip types is selected, or the date window has moved off the last two months. An
     * empty trip-type set and the full set both mean "all", so only a proper subset counts.
     */
    val isFiltered: Boolean
        get() {
            if (filter.equipmentId != null) return true
            if (filter.employeeId != null) return true
            if (filter.tripTypes.isNotEmpty() && filter.tripTypes.size < TripType.entries.size) return true
            val (defaultStart, defaultEnd) = Util.lastTwoMonthsRange()
            return filter.startDate != defaultStart || filter.endDate != defaultEnd
        }

    /**
     * Whether the current user may edit/delete [entry]. ADMIN/DISPATCHER always can; everyone
     * else (including a not-yet-loaded null [currentUser]) is locked out of entries whose trip
     * date is more than 7 calendar days old (system timezone).
     */
    fun canModify(entry: MileageEntry): Boolean {
        if (isElevated) return true
        return entry.date.date >= Util.localDateTime().date.minus(7, DateTimeUnit.DAY)
    }
}
